import React, { useState } from 'react';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { toast } from 'sonner';
import { usePdfExport } from '../../hooks/usePdfExport';
import type { PerformanceService } from '../../api/types';
import { formatCurrency } from '../../utils/format';

interface Props {
  open: boolean;
  onClose: () => void;
  services: PerformanceService[];
  externalPhotographerId?: string;
  externalPhotographerName?: string;
}

export function ServiceExportPdfDrawer({ open, onClose, services, externalPhotographerId, externalPhotographerName }: Props) {
  const activeServices = services.filter(s => s.isActive);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(activeServices.map(s => s.id)));
  const [showDescription, setShowDescription] = useState(true);
  const [showBasePrice, setShowBasePrice] = useState(true);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const { isExporting, exportPdf } = usePdfExport();
  const isExternalMode = !!externalPhotographerId;

  const toggleAll = () => {
    if (selectedIds.size === activeServices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(activeServices.map(s => s.id)));
    }
  };

  const toggleService = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const handleExport = async () => {
    if (selectedIds.size === 0) {
      toast.error('Seleziona almeno un servizio');
      return;
    }
    const ok = await exportPdf('/api/performances/export-pdf', {
      serviceIds: [...selectedIds],
      showDescription,
      showBasePrice,
      validUntil: validUntil || undefined,
      notes: notes || undefined,
      ...(isExternalMode ? { externalPhotographerId } : {}),
    }, 'listino-servizi.pdf');
    if (ok) onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Esporta PDF Listino Servizi${isExternalMode ? ` — ${externalPhotographerName}` : ''}`}
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={handleExport} loading={isExporting}>Genera PDF</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* Service selection */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              checked={selectedIds.size === activeServices.length}
              ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < activeServices.length; }}
              onChange={toggleAll}
              style={{ accentColor: 'var(--color-primary)' }}
            />
            Seleziona tutti ({selectedIds.size}/{activeServices.length})
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: 280, overflowY: 'auto' }}>
            {activeServices.map(s => (
              <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0', fontSize: 'var(--text-sm)', color: 'var(--color-text)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(s.id)}
                  onChange={() => toggleService(s.id)}
                  style={{ accentColor: 'var(--color-primary)' }}
                />
                {s.name} — {formatCurrency(s.basePrice)}
              </label>
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
            <input type="checkbox" checked={showDescription} onChange={e => setShowDescription(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
            Mostra descrizione servizio
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
            <input type="checkbox" checked={showBasePrice} onChange={e => setShowBasePrice(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
            Mostra prezzo base
          </label>
        </div>

        {/* Valid until */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.375rem' }}>Valido fino al</label>
          <input
            type="date"
            value={validUntil}
            onChange={e => setValidUntil(e.target.value)}
            style={{
              width: '100%', background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)', fontSize: 'var(--text-sm)',
              padding: '0.5rem 0.75rem', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Notes */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.375rem' }}>
            {isExternalMode ? 'Condizioni commerciali riservate (max 500 caratteri)' : 'Note (max 500 caratteri)'}
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value.slice(0, 500))}
            rows={3}
            placeholder={isExternalMode ? 'Condizioni riservate per il fotografo esterno...' : 'Note da includere sotto l\'intestazione...'}
            style={{
              width: '100%', background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)', fontSize: 'var(--text-sm)',
              padding: '0.5rem 0.75rem', resize: 'vertical', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>
    </Drawer>
  );
}
